<template>
  <div class="award-group-review">
    <sme-card v-if="hasBalance && combinedBudgetMinor > 0" class="award-group-review__balance">
      <div class="award-group-review__balance-header">
        <span class="award-group-review__balance-title">Budget</span>
        <span v-if="!withinBalance" class="award-group-review__balance-warning">
          <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="mr-1" />
          This exceeds your available balance of {{ formatMoney(balanceMinor) }}
        </span>
      </div>
      <b-progress
        :max="balanceMinor"
        class="award-group-review__balance-bar"
        :variant="withinBalance ? 'primary' : 'danger'"
      >
        <b-progress-bar :value="Math.min(combinedBudgetMinor, balanceMinor)" />
      </b-progress>
      <div class="award-group-review__balance-footer">
        <span>{{ formatMoney(combinedBudgetMinor) }} combined annual budget</span>
        <span>{{ remainingLabel }}</span>
      </div>
    </sme-card>

    <div class="award-group-review__table-card">
      <awards-table :programs="previewPrograms" readonly @toggle-active="handleToggleActive" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { toCurrency } from '@/utils';
import SmeCard from '@/components/atoms/SmeCard.vue';
import AwardsTable from '@/pages/recognition/components/AwardsTable.vue';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  divisionOptions: { type: Array, default: () => [] },
  employeeOptions: { type: Array, default: () => [] },
  balanceMinor: { type: Number, default: null },
  combinedBudgetMinor: { type: Number, default: 0 },
  groupName: { type: String, default: '' },
});

const emit = defineEmits(['toggle-active']);

// previewPrograms ids are `preview-<index>`; map back to the row index for the parent.
function handleToggleActive({ programId }) {
  const index = parseInt(String(programId).replace('preview-', ''), 10);
  if (Number.isFinite(index)) {
    emit('toggle-active', index);
  }
}

const divisionNameById = computed(() => {
  const map = new Map();
  for (const opt of props.divisionOptions) {
    map.set(String(opt.value), opt.text);
  }
  return map;
});

const employeeNameById = computed(() => {
  const map = new Map();
  for (const opt of props.employeeOptions) {
    // Option text is "Name - email"; show just the name in the summary.
    map.set(String(opt.value), opt.text.split(' - ')[0]);
  }
  return map;
});

function formatMoney(minor) {
  return toCurrency(minor || 0);
}

const hasBalance = computed(() => props.balanceMinor !== null && props.balanceMinor !== undefined);
const withinBalance = computed(() => props.combinedBudgetMinor <= (props.balanceMinor || 0));
const remainingLabel = computed(() => {
  const remaining = (props.balanceMinor || 0) - props.combinedBudgetMinor;
  if (remaining >= 0) {
    return `${formatMoney(remaining)} remaining of ${formatMoney(props.balanceMinor)}`;
  }
  return `${formatMoney(-remaining)} over your ${formatMoney(props.balanceMinor)} balance`;
});

// Map editor rows to the program-like shape AwardsTable renders. IDs are synthetic
// (rows aren't persisted yet) and only used for tooltip/popover targeting.
const previewPrograms = computed(() =>
  props.rows.map((row, index) => {
    const divisions = (row.enabled_divisions || []).map(String);
    const approvers = row.approval_type === 'NONE' ? [] : (row.approvers || []).map(String);
    return {
      recognition_award_program_id: `preview-${index}`,
      // Group all previews under the destination group so the table shows a group header.
      recognition_award_group_id: 'preview-group',
      recognition_award_group_name: props.groupName,
      program_name: row.program_name,
      program_type: row.program_type,
      is_active: row.is_active ?? true,
      enabled_divisions: divisions,
      division_names: divisions.map(id => divisionNameById.value.get(id)).filter(Boolean),
      nominator_names: (row.nominators || []).map(id => employeeNameById.value.get(String(id))).filter(Boolean),
      approver_names: approvers.map(id => employeeNameById.value.get(id)).filter(Boolean),
      budget_minor: row.monetary_enabled ? row.budget_minor : null,
      budget_spent_minor: 0,
      badge_presigned_url: row.badge_presigned_url || '',
      properties: {
        budget_period: row.monetary_enabled ? row.budget_cadence : null,
        short_description: row.short_description || '',
      },
    };
  }),
);
</script>

<style scoped lang="scss">
.award-group-review {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__balance-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.75rem;
  }

  &__balance-title {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--palette-color-default);
  }

  &__balance-warning {
    font-size: 0.8125rem;
    color: var(--palette-color-danger);
    text-align: right;
  }

  &__balance-bar {
    height: 0.625rem;
    border-radius: 0.375rem;
  }

  &__balance-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-20);
  }

  &__table-card {
    background: var(--palette-color-base-white);
    border: 1px solid var(--palette-color-default-lighten-90);
    border-radius: 0.5rem;
    overflow: hidden;
  }
}
</style>
